import t from "@/lang/lang";
import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { PositionKind } from "./position-kind";

/** Mirrors `positions.PriceUsed`, backend/core/omni/domain/positions/price_used.go. */
export const PriceUsed = {
  Zero: "",
  UnitPrice: "unitPrice",
  FinalPrice: "finalPrice",
  SubscriptionPrice: "subscriptionPrice",
} as const;

export type PriceUsed = (typeof PriceUsed)[keyof typeof PriceUsed];

/**
 * PricesUsed are the prices a position may show on documents, besides
 * the default, and the one it shows by default.
 */
export type PricesUsed = {
  allowed: PriceUsed[];
  byDefault: PriceUsed;
};

/**
 * PricesUsedOf returns the {@link PricesUsed} of a position of kind and
 * serviceVariation, a rule of the service kind.
 */
export type PricesUsedOf = (
  kind: PositionKind,
  serviceVariation: ServiceVariation,
) => PricesUsed;

const noPricesUsed: PricesUsed = { allowed: [], byDefault: PriceUsed.Zero };

/**
 * pricesUsedOfKind returns the {@link PricesUsed} of a position of kind:
 * none for a position showing no price of its own (a text, a title, a sum
 * or a modifier), the unit price for a dynamic position, and those of
 * priced for any other, see `pricesUsed`,
 * backend/core/omni/domain/servicekindrules/prices_used.go.
 */
export function pricesUsedOfKind(
  kind: PositionKind,
  serviceVariation: ServiceVariation,
  priced: (serviceVariation: ServiceVariation) => PricesUsed,
): PricesUsed {
  switch (kind) {
    case PositionKind.Text:
    case PositionKind.GroupTitle:
    case PositionKind.AreaTitle:
    case PositionKind.SubTotal:
    case PositionKind.Total:
    case PositionKind.PriceModifierRelative:
    case PositionKind.PriceModifierAbsolute:
      return noPricesUsed;
    case PositionKind.Dynamic:
      return {
        allowed: [PriceUsed.UnitPrice],
        byDefault: PriceUsed.UnitPrice,
      };
    default:
      return priced(serviceVariation);
  }
}

/** priceUsedLabel returns the name of the price for users. */
export function priceUsedLabel(price: PriceUsed): string {
  switch (price) {
    case PriceUsed.UnitPrice:
      return t("Preis / Einheit");
    case PriceUsed.FinalPrice:
      return t("Endpreis");
    case PriceUsed.SubscriptionPrice:
      return t("Abo-Preis");
    default:
      return "";
  }
}

// A select item cannot have the empty value of the default.
const defaultSelectValue = "default";

/**
 * priceUsedSelectItems returns the items selecting the price used:
 * "Standard" naming the default, then the allowed prices.
 */
export function priceUsedSelectItems(
  prices: PricesUsed,
): { value: string; label: string }[] {
  const byDefault = priceUsedLabel(prices.byDefault);
  return [
    {
      value: defaultSelectValue,
      label: byDefault ? `${t("Standard")} (${byDefault})` : t("Standard"),
    },
    ...prices.allowed.map((price) => ({
      value: price,
      label: priceUsedLabel(price),
    })),
  ];
}

/** priceUsedToSelectValue returns the value of price in the select items. */
export function priceUsedToSelectValue(price: PriceUsed | undefined): string {
  return !price ? defaultSelectValue : price;
}

/** priceUsedFromSelectValue returns the price of a select item value. */
export function priceUsedFromSelectValue(value: string): PriceUsed {
  return value === defaultSelectValue ? PriceUsed.Zero : (value as PriceUsed);
}
